import { useId, useState } from "react";
import type { DatePickerButtonProps } from "@b2bportal/core-flights";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import defaultStyles from "./DatePickerButton.module.scss";

interface ContentProps {
  label: string;
  date: string | null;
  placeholder: string;
}

export const DatePickerButton = (props: DatePickerButtonProps) => {
  const {
    startDate,
    endDate,
    startLabel,
    endLabel,
    disabled,
    renderCalendarPopup,
    onClick,
    dateFormat,
    tabIndex,
    buttonClass,
    hideEndDate,
    hideSeparator,
    ariaLabel,
    classes = [],
    error,
  } = props;

  const styles = useFlightStyles(
    CoreFlightsComponent.DatePickerButton,
    defaultStyles
  );

  const [block, cn] = useModuleBEM(
    styles,
    CoreFlightsComponent.DatePickerButton
  );

  const [openModal, setOpenModal] = useState<boolean>(false);
  const errorId = useId();

  const { formatDateTime, DateTimeFormatStyle, t } = useI18nContext();
  const defaultDateTimeFormat =
    DateTimeFormatStyle.YearShortMonthDayShortWeekday;

  const handleClick = () => {
    setOpenModal(true);
    onClick?.();
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const calendarIcon = <Icon iconName={IconNameEnum.calendar} />;

  const Content = ({ date, label, placeholder }: ContentProps) => (
    <div className={cn("button-content")}>
      <p className={cn("content-top-label")}>{label}</p>
      <p className={clsx(cn("content-main-content", { label: true }))}>
        {date
          ? formatDateTime(
              dayjs(date).toDate(),
              dateFormat ?? defaultDateTimeFormat
            )
          : placeholder}
      </p>
    </div>
  );

  const startDateComponent = (
    <div className={cn("start-date-container", { error: error != null })}>
      {calendarIcon}
      <Content
        date={startDate}
        label={startLabel}
        placeholder={t("core-ui.date.placeholders.startDate")}
      />
    </div>
  );

  const endDateComponent = (
    <div className={cn("end-date-container", { error: error != null })}>
      {calendarIcon}
      <Content
        date={endDate}
        label={endLabel}
        placeholder={t("core-ui.date.placeholders.endDate")}
      />
    </div>
  );

  const separatorIcon = (
    <div className={cn("separator-container")}>
      <Icon iconName={IconNameEnum.calendarSeparatorIcon} />
    </div>
  );

  return (
    <div className={clsx(cn("container"), ...classes)}>
      <button
        className={clsx(
          cn("start-button", {
            error: error != null,
          }),
          buttonClass
        )}
        aria-label={ariaLabel}
        aria-describedby={error != null ? errorId : undefined}
        type="button"
        disabled={disabled}
        onClick={handleClick}
        tabIndex={tabIndex}
      >
        {startDateComponent}
        {!hideEndDate && !hideSeparator && separatorIcon}
        {!hideEndDate && endDateComponent}
      </button>
      {error != null && (
        <VisuallyHidden asChild>
          <div id={errorId} role="alert">
            {t("requiredField")}
          </div>
        </VisuallyHidden>
      )}
      {renderCalendarPopup &&
        renderCalendarPopup({
          open: openModal,
          closePopup: handleClose,
        })}
    </div>
  );
};
